import {
	doPOST,
	type AlternativeForm,
	isInvalidTotalAmountForVariety,
	type OrderLine,
	shoppingCartStorage,
	shopRestBackendBaseUrl
} from 'dummen-shared';
import {type ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';
import {orderLineIsSurplus} from '@/service/order.line.service.ts';

const getAvailabilityForOrderLine = (availabilities: OrderLine[], orderLine: ShoppingCartOrderLine): number => {
	const availability = availabilities.find((availLine: OrderLine) => isAvailabilityLineForOrderLine(availLine, orderLine));
	return availability ? availability.availability : 0
};

const getAlternativeFormsForOrderLine = (availabilities: OrderLine[], orderLine: ShoppingCartOrderLine): AlternativeForm[] => {
	const availability = availabilities.find((availLine: OrderLine) => isAvailabilityLineForOrderLine(availLine, orderLine));
	return availability?.alternativeForms ?? [];
};

const isAvailabilityLineForOrderLine = (availLine: OrderLine, orderLine: ShoppingCartOrderLine): boolean => {
	return availLine.articleId === orderLine.articleId &&
		availLine.week === orderLine.week &&
		availLine.year === orderLine.year &&
		// An order line without a location can hold either null or undefined, the response always holds null.
		(availLine.locationId ?? null) === (orderLine.locationId ?? null) &&
		availLine.formId === orderLine.formId;
}


const mapToPatAvailabilityRequestLine = (orderLine: ShoppingCartOrderLine) => {
	return {
		formId: orderLine.formId,
		articleId: orderLine.articleId,
		locationId: orderLine.locationId ?? null,
		week: orderLine.week,
		year: orderLine.year,
		amount: orderLine.amount
	};
};

const mapToIndexAvailabilityRequestLine = (orderLine: ShoppingCartOrderLine) => {
	return {
		formId: orderLine.formId,
		articleId: orderLine.articleId,
		locationId: orderLine.locationId ?? null,
		week: orderLine.week,
		year: orderLine.year
	};
};

/**
 * Upper bound for the number of varieties (patcode + form combinations) in a single backend request.
 * Limits the varieties competing for the shared PAT concurrency cap across two simultaneous batches.
 */
const MAX_VARIETIES_PER_BATCH = 6;

/**
 * Lower bound for the batch size, so small orders are not split into a request per variety.
 */
const MIN_VARIETIES_PER_BATCH = 2;

/**
 * Number of batches aimed for, so the progress bar moves in reasonable steps instead of jumping from 0 to 100%.
 */
const TARGET_BATCH_COUNT = 8;

/**
 * Number of batches that are sent to the backend at the same time.
 */
const MAX_PARALLEL_BATCHES = 2;

const PAT_AVAILABILITY_URL = `${shopRestBackendBaseUrl}/app/availabilities-create-order`;

export interface AvailabilityCheckResult {
	/** Total number of order lines that were part of the live check. */
	total: number;
	/** Number of order lines that were actually checked (less than total when cancelled). */
	checked: number;
	/** Number of order lines that could not be checked because their batch failed. */
	failed: number;
	/** True when the check was stopped by the user before all batches were done. */
	cancelled: boolean;
	/** All shortages found during this check. */
	shortages: AvailabilityShortage[];
}

/**
 * An order line for which the live availability is lower than the ordered amount.
 */
export interface AvailabilityShortage {
	articleId: string;
	articleName: string;
	formId: string;
	formName: string;
	groupName: string;
	week: number;
	year: number;
	amount: number;
	amountAvailable: number;
}

export interface AvailabilityCheckOptions {
	/** Order lines to check; defaults to all non-surplus lines in the shopping cart. */
	orderLines?: ShoppingCartOrderLine[];
	/** Called after every finished batch with the number of checked order lines and the total. */
	onProgress?: (checked: number, total: number) => void;
	/** Called after every finished batch with the shortages found in that batch, so they can be shown immediately. */
	onShortages?: (shortages: AvailabilityShortage[]) => void;
	/** Called before each batch; resolves false to stop, or waits until the user resumes the check. */
	waitForBatch?: () => Promise<boolean>;
	/** Stops the check: batches that have not started yet are skipped. */
	signal?: AbortSignal;
}

/**
 * Groups order lines that resolve to the same availability document (patcode + form).
 * All weeks of one variety stay together, so they are never checked by two concurrent requests.
 */
export const groupOrderLinesByVariety = (orderLines: ShoppingCartOrderLine[]): ShoppingCartOrderLine[][] => {
	const groups = new Map<string, ShoppingCartOrderLine[]>();

	orderLines.forEach(orderLine => {
		const key = `${orderLine.articleId}|${orderLine.formId}`;
		const group = groups.get(key);

		if (group) {
			group.push(orderLine);
		} else {
			groups.set(key, [orderLine]);
		}
	});

	return [...groups.values()];
};

/**
 * Determines the batch size: small enough to let the progress bar move in steps, but never so small that the order
 * results in an excessive number of requests.
 */
export const determineVarietiesPerBatch = (varietyCount: number): number => {
	const batchSize = Math.ceil(varietyCount / TARGET_BATCH_COUNT);

	return Math.min(MAX_VARIETIES_PER_BATCH, Math.max(MIN_VARIETIES_PER_BATCH, batchSize));
};

/**
 * Splits the variety groups into batches of at most {@link MAX_VARIETIES_PER_BATCH} varieties.
 */
export const createAvailabilityBatches = (
	varietyGroups: ShoppingCartOrderLine[][],
	varietiesPerBatch: number = determineVarietiesPerBatch(varietyGroups.length)
): ShoppingCartOrderLine[][] => {
	if (varietyGroups.length === 0) {
		return [];
	}

	const batches: ShoppingCartOrderLine[][] = [varietyGroups[0]];

	for (let index = 1; index < varietyGroups.length; index += varietiesPerBatch) {
		batches.push(varietyGroups.slice(index, index + varietiesPerBatch).flat());
	}

	return batches;
};

const applyAvailabilities = (availabilities: OrderLine[], batch: ShoppingCartOrderLine[]) => {
	if (!shoppingCartStorage.value.orderLines?.length) {
		return;
	}

	const batchKeys = new Set(batch.map(orderLine => orderLineKey(orderLine)));

	shoppingCartStorage.value.orderLines = shoppingCartStorage.value.orderLines.map(orderLine => {
		if (!batchKeys.has(orderLineKey(orderLine))) {
			// Not part of this batch: leave it untouched so results of other batches are not overwritten.
			return orderLine;
		}

		return {
			...orderLine,
			amountAvailable: getAvailabilityForOrderLine(availabilities, orderLine)
		};
	});
};

const orderLineKey = (orderLine: ShoppingCartOrderLine): string => {
	return `${orderLine.articleId}|${orderLine.formId}|${orderLine.locationId}|${orderLine.year}|${orderLine.week}`;
};

/** Outcome of a single batch request, used to tell a user-initiated abort apart from a genuine failure. */
type BatchOutcome = 'succeeded' | 'failed' | 'aborted';

const checkBatch = async (batch: ShoppingCartOrderLine[], signal?: AbortSignal): Promise<BatchOutcome> => {
	const orderLines = batch.map(orderLine => mapToPatAvailabilityRequestLine(orderLine));

	try {
		const response = await doPOST(PAT_AVAILABILITY_URL, JSON.stringify({orderLines}), signal);
		applyAvailabilities(await response.json(), batch);

		return 'succeeded';

	} catch (error) {
		if (error instanceof DOMException && error.name === 'AbortError') {
			// Stopped by the user: discard this batch's (missing) result instead of counting it as failed.
			return 'aborted';
		}

		// The backend falls back to the indexed availability per article, so the remaining batches are still useful.
		console.warn('Live availability check failed for a batch of order lines', error);

		return 'failed';
	}
};

/**
 * Collects the order lines of the given batch that no longer have enough availability, based on the values that were
 * just stored in the shopping cart.
 */
const collectShortages = (batch: ShoppingCartOrderLine[]): AvailabilityShortage[] => {
	const allOrderLines = shoppingCartStorage.value.orderLines ?? [];
	const batchKeys = new Set(batch.map(orderLine => orderLineKey(orderLine)));

	return allOrderLines
		.filter(orderLine => batchKeys.has(orderLineKey(orderLine)))
		.filter(orderLine => hasShortage(orderLine, allOrderLines))
		.map(orderLine => ({
			articleId: orderLine.articleId,
			articleName: orderLine.articleName,
			formId: orderLine.formId,
			formName: orderLine.formName,
			groupName: orderLine.groupName,
			week: orderLine.week,
			year: orderLine.year,
			amount: orderLine.amount,
			amountAvailable: orderLine.amountAvailable
		}));
};

const hasShortage = (orderLine: ShoppingCartOrderLine, allOrderLines: ShoppingCartOrderLine[]): boolean => {
	if (orderLine.allowOverbooking) {
		return false;
	}

	return orderLine.amount > orderLine.amountAvailable
		|| isInvalidTotalAmountForVariety(orderLine, allOrderLines, orderLine.amountAvailable);
};

/**
 * Fetches and updates the availability for all non-surplus order lines with realtime data from PAT.
 * The order lines are checked in batches so a single request never runs into the CloudFront timeout; shortages are
 * reported per batch so the user can act on them while the check is still running.
 *
 * @param options progress/shortage callbacks and the abort signal used to stop the check.
 */
export const updateAvailabilityFromPAT = async (
	options: AvailabilityCheckOptions = {}
): Promise<AvailabilityCheckResult> => {
	const {orderLines: requestedOrderLines, onProgress, onShortages, signal, waitForBatch} = options;

	const orderLines = (requestedOrderLines ?? shoppingCartStorage.value.orderLines)
		?.filter(orderLine => !orderLineIsSurplus(orderLine.week, orderLine.year)) ?? [];

	const total = orderLines.length;

	if (total === 0) {
		onProgress?.(0, 0);
		return {total: 0, checked: 0, failed: 0, cancelled: false, shortages: []};
	}

	const batches = createAvailabilityBatches(groupOrderLinesByVariety(orderLines));

	const shortages: AvailabilityShortage[] = [];
	let checked = 0;
	let failed = 0;

	onProgress?.(checked, total);

	const nextBatchIndex = createBatchIndexSupplier();

	const runBatches = async () => {
		while (!signal?.aborted) {
			const canContinue = await waitForBatch?.() ?? true;
			if (!canContinue || signal?.aborted) {
				return;
			}

			const index = nextBatchIndex();
			if (index >= batches.length) {
				return;
			}

			const batch = batches[index];
			const outcome = await checkBatch(batch, signal);

			if (outcome === 'aborted') {
				// The request was cancelled in-flight: its (missing) result doesn't count towards checked/failed.
				return;
			}

			if (outcome === 'succeeded') {
				const batchShortages = collectShortages(batch);

				if (batchShortages.length > 0) {
					shortages.push(...batchShortages);
					onShortages?.(batchShortages);
				}
			} else {
				failed += batch.length;
			}

			checked += batch.length;
			onProgress?.(checked, total);
		}
	};

	await Promise.all(
		Array.from({length: Math.min(MAX_PARALLEL_BATCHES, batches.length)}, () => runBatches())
	);

	return {total, checked, failed, cancelled: checked < total, shortages};
};

const createBatchIndexSupplier = () => {
	let index = -1;

	return (): number => {
		index += 1;
		return index;
	};
};

/**
 * Fetches and updates the availability for all order lines with the latest data from ElasticSearch.
 */
export const updateAvailabilityFromIndex = async () => {
	if (!shoppingCartStorage.value.orderLines?.length) {
		return;
	}

	const orderLines = shoppingCartStorage.value.orderLines
		.map((line: ShoppingCartOrderLine) => mapToIndexAvailabilityRequestLine(line));

	const response = await doPOST(
		`${shopRestBackendBaseUrl}/app/availabilities`,
		JSON.stringify({orderLines})
	);

	const availabilities = await response.json();

	shoppingCartStorage.value.orderLines = shoppingCartStorage.value.orderLines.map(orderLine => {
		return {
			...orderLine,
			amountAvailable: getAvailabilityForOrderLine(availabilities, orderLine),
			alternativeForms: getAlternativeFormsForOrderLine(availabilities, orderLine)
		};
	});
};
